<template>
    <main class="results-table-container"
          :class="{'selection-mode-on': recordsSelectionModeOn, 'filter-menu-visible': activeFilterMenuProperty}">
        <div class="fake-table-header-container" ref="fakeTableHeaderContainer">
            <table ref="fakeTable">
                <thead>
                <tr>
                    <th>
                        <div class="content"></div>
                    </th>
                    <th v-for="(property) in shownProperties" :key="property.propertyKey"
                        :class="getPropertyClassSet(property)"
                        :data-tooltip="property.tooltipText" @mouseenter="onMouseEnterTooltipTarget"
                        :data-tooltip-x-offset="property.tooltipOffset"
                        @mouseleave="onMouseLeaveTooltipTarget" data-tooltip-pos="up">
                        <div class="content">
                            <span class="text">{{property.label}}</span>
                            <div class="filter-label"
                                 v-if="propertyFilterFormValues[property.propertyKey]"
                                 v-html="property.filterable.labelRenderer(propertyFilterFormValues[property.propertyKey])"></div>
                            <div class="sortable-indicator" v-if="property.sortable">
                                <div class="vertical-container">
                                    <i class="sort-asc gf gf-arrow-up"></i>
                                    <i class="sort-desc gf gf-arrow-down"></i>
                                </div>
                            </div>
                            <div class="operation-layer" v-if="property.sortable||property.filterable">
                                <div class="text">{{property.label}}</div>
                                <div class="buttons">
                                    <template v-if="property.sortable">
                                        <i @click="sortByProperty(property,'asc')"
                                           class="button-sort sort-asc gf gf-arrow-up"></i>
                                        <i @click="sortByProperty(property, 'desc')"
                                           class="button-sort sort-desc gf gf-arrow-down"></i>
                                    </template>
                                    <simple-dropdown v-if="property.filterable"
                                                     :ref="'filterFormDropdownFor'+property.propertyKey"
                                                     :click-dropdown-menu-to-hide="false"
                                                     :dropdown-close-corner="true"
                                                     :dropdown-overflow="null"
                                                     @dropdownMenuShown="onPropertyFilterPopupVisibilityChange(property, true)"
                                                     @dropdownMenuHidden="onPropertyFilterPopupVisibilityChange(property, false)">
                                        <template v-slot:triggerButtonContent>
                                            <i class="button-filter gf gf-filter"></i>
                                        </template>
                                        <template v-slot:dropdownMenu>
                                            <div class="dropdown-content">
                                                <div class="filter-form-wrapper">
                                                    <component :is="property.filterable.filterForm"
                                                               :ref="'filterFormFor'+property.propertyKey"
                                                               :property="property"
                                                               :parameters="property.filterable.parameters"
                                                               @doClose="onPropertyFormDoClose"
                                                               @doSubmit="onPropertyFormDoSubmit"
                                                    ></component>
                                                </div>
                                            </div>
                                        </template>
                                    </simple-dropdown>
                                </div>
                            </div>
                        </div>
                    </th>
                </tr>
                </thead>
            </table>
        </div>
        <div class="scrolling-element" ref="scrollingElement" @scroll="onTableScroll">
            <table ref="table">
                <thead style="visibility: hidden;pointer-events: none">
                <tr>
                    <th>
                        <div class="content">

                        </div>
                    </th>
                    <th v-for="(property) in shownProperties" :key="property.propertyKey"
                        :class="getPropertyClassSet(property)">
                        <div class="content">
                            <span class="text">{{property.label}}</span>
                        </div>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, index) in rows" :key="index"
                    @click="onClickResultsRow(row, $event)"
                    :class="{open: openGlobalDocumentIds.indexOf(row.global_document_id)!==-1, selected: selectedGlobalDocumentIds.indexOf(row.global_document_id)!==-1}">
                    <td>
                        <div class="content position-relative">
                            <div class="line justify-content-end">
                                <label> {{index + 1 + rowCounterBase}}</label>
                            </div>
                            <div class="selection-tool" v-if="recordsSelectionModeOn">
                                <input type="checkbox" @change="onRecordCheckboxChange($event, row)"
                                       ref="recordCheckboxes">
                            </div>
                        </div>
                    </td>
                    <td v-for="(property) in shownProperties"
                        :key="property.propertyKey"
                        :class="property.className"
                    >
                        <div class="content">
                            <template v-if="property.renderHTML">
                                <div v-html="property.renderer(row, projectTemplate)"></div>
                            </template>
                            <div class="line" v-else>{{property.renderer(row, projectTemplate)}}</div>
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <div class="tbody-mask"></div>
    </main>
</template>

<script>
import {
    additionalDocumentProperties,
    documentStatusIdToLabel,
    projectIdToTemplate,
    statusHTMLRenderer,
    statusIds
} from "@/config";
import {
    caseInsensitiveSortFn,
    checkCrossPlatformEventCtrlKey,
    deepCompare,
    describeUnixTs,
    evaluateFieldValueFilled,
    renderValueInCandidateItem
} from "@/utils";
import $ from 'jquery';
import SimpleDropdown from "@/components/SimpleDropdown";
import TextMessage from "@/components/FilterForms/TextMessage";
import CheckBoxes from "@/components/FilterForms/CheckBoxes";
import {Promise} from 'es6-promise-polyfill';
import {getUserList} from "@/apis";

const tableCellLineHeightBase = 36;

    const propertyValueCategories = {
        filled_confirmed: 0,
        auto_filled_unconfirmed: 1,
        unfilled_with_candidates: 2,
        unfilled_without_candidates: 3
    };

    const getBaseProperties = function (enableSerial) {
        let baseProperties = [
            {
                label: 'Document ID',
                renderer(item) {
                  const isFinalResults = item['custom_data']['subtypes_selected'].indexOf('Final Results') !== -1;
                    return `<div class="line"><div class="document-entry document-entry-pointer document-entry-pointer-dominant">
                        ${item['document_id']}</div>${isFinalResults ? '<span title="Final Results" class="final-results-indicator">(F)</span>' : ''}</div>`
                },
                renderHTML: true,
                sortable: {
                    asc: {'document_id': 1},
                    desc: {'document_id': -1},
                },
                filterable: {
                    pipsBuilder(payload) {
                        return [{$match: {'document_id': payload.value}}]
                    },
                    labelRenderer(payload) {
                        return `<span>${payload.value}</span>`;
                    },
                    filterForm: TextMessage,
                }
            },
        ];

        baseProperties = baseProperties.concat(additionalDocumentProperties(enableSerial)).concat([
            {
                label: 'Status',
                renderer(item) {
                    let padding = '';
                    let statusSetTime;
                    if ((statusSetTime = item['status_set_time'])) {
                        padding = `title="${describeUnixTs(statusSetTime)}"`;
                    }
                    return `<div class="text-center mx-1" ${padding}>${statusHTMLRenderer(item['status'])}</div>`;
                },
                renderHTML: true,
                filterable: {
                    pipsBuilder(payload) {
                        let $or = payload.selectedItems.map(v => ({status: v}));
                        return [
                            {
                                $match: {
                                    $or
                                }
                            }
                        ]
                    },
                    labelRenderer(payload) {
                        return `<span>${payload.selectedItems.map(v => (documentStatusIdToLabel[v])).join(', ')}</span>`;
                    },
                    filterForm: CheckBoxes,
                    parameters: {
                        items: [
                            {
                                label: 'Todo',
                                id: statusIds.todo,
                                labelHTML: `<span class="d-flex align-items-center">${statusHTMLRenderer(statusIds.todo)} <span class="status-text">Todo</span></span>`
                            },
                            {
                                label: 'Done',
                                id: statusIds.done,
                                labelHTML: `<span class="d-flex align-items-center">${statusHTMLRenderer(statusIds.done)} <span class="status-text">Done</span></span>`
                            },
                            {
                                label: 'Skipped',
                                id: statusIds.skipped,
                                labelHTML: `<span class="d-flex align-items-center">${statusHTMLRenderer(statusIds.skipped)} <span class="status-text">Skipped</span></span>`
                            }
                        ]
                    }
                },
                exportValue(item) {
                    return documentStatusIdToLabel[item['status']];
                },
            },
        ]);

        const propertyStatusSetter = {
            label: 'User',
            renderer(item) {
                const statusSetBy = item['status_set_by'] || '';
                const container = elementGenerators.tableCellMultiLines();
                let elem;
                elem = elementGenerators.tableCellLine(statusSetBy, tableCellLineHeightBase, 'text-capitalize');
                container.appendChild(elem);
                return container.outerHTML;
            },
            renderHTML: true,
            sortable: {
                asc: {'status_set_by': 1},
                desc: {'status_set_by': -1},
            },
            className: 'text-center',
            filterable: {
                pipsBuilder(payload) {
                    let $or = payload.selectedItems.map(v => ({status_set_by: v}));
                    return [
                        {
                            $match: {
                                $or
                            }
                        }
                    ]
                },
                labelRenderer(payload) {
                    return `<span class="text-capitalize">${payload.selectedItems.join(', ')}</span>`;
                },
                filterForm: CheckBoxes,
                parameters: {
                    items: [],
                    promisedItems: new Promise((resolve) => {
                        getUserList().then(r => {
                            let userList = r.data;
                            userList.sort(caseInsensitiveSortFn);
                            userList = userList.map(v => ({id: v, label: v}))
                            resolve(userList);
                        })
                    }),
                    labelClassName: 'text-capitalize',
                }
            },
        };

        baseProperties.push(propertyStatusSetter);

        if (enableSerial) {
            baseProperties = baseProperties.concat([
                {
                    label: 'Serial ID',
                    renderer(item) {
                        const serials = item['serials'] || [];
                        const container = elementGenerators.tableCellMultiLines();
                        let elem;
                        serials.forEach((serial) => {
                            elem = elementGenerators.tableCellLine(serial['serial_id'], tableCellLineHeightBase * (serial['events'].length || 1));

                            elem.className = elem.className += ' document-entry document-entry-pointer';
                            elem.dataset.documentEntryFocusSerialId = serial['serial_id'];

                            container.appendChild(elem);
                        });
                        return container.outerHTML;
                    },
                    renderHTML: true,
                    filterable: {
                        pipsBuilder(payload) {
                            return [{$match: {'serial_ids': payload.value}}]
                        },
                        labelRenderer(payload) {
                            return `<span>${payload.value}</span>`;
                        },
                        filterForm: TextMessage,
                    },
                    sortable: {
                        asc: {'serial_ids': 1},
                        desc: {'serial_ids': -1},
                    },
                },
                {
                    label: 'Event ID',
                    renderer(item) {
                        const serials = item['serials'] || [];
                        const container = elementGenerators.tableCellMultiLines();
                        let elem;

                        serials.forEach((serial) => {
                            if (!serial['events'].length) {
                                container.appendChild(elementGenerators.tableCellLine('', tableCellLineHeightBase));
                                return;
                            }
                            (serial['events']).forEach((event) => {
                                elem = elementGenerators.tableCellLine(event['event_id'], tableCellLineHeightBase);
                                elem.className = elem.className += ' document-entry document-entry-pointer';
                                elem.dataset.documentEntryFocusEvent = event['event_id'];

                                container.appendChild(elem)
                            });
                        });
                        return container.outerHTML;
                    },
                    renderHTML: true,
                    filterable: {
                        pipsBuilder(payload) {
                            return [{$match: {'joined_event_objects.event_id': payload.value}}]
                        },
                        labelRenderer(payload) {
                            return `<span>${payload.value}</span>`;
                        },
                        filterForm: TextMessage,
                        involvesJoinedEventObjects: true
                    },
                    sortable: {
                        asc: {'joined_event_objects.event_id': 1},
                        desc: {'joined_event_objects.event_id': -1},
                      involvesJoinedEventObjects: true
                    },
                },
                {
                    label: 'Statistics',
                    renderer(item) {
                        let projectTemplate;

                        projectTemplate = projectIdToTemplate[item['project_id']];

                        if (!projectTemplate) {
                            return '';
                        }

                        const serials = item['serials'] || [];

                        const preprocessingResults = item['preprocessing_results'] || {};

                        const container = elementGenerators.tableCellMultiLines();
                        serials.forEach((serial) => {
                            if (!serial['events'].length) {
                                container.appendChild(generateLineForStatistics(projectTemplate, preprocessingResults, null));
                                return
                            }
                            (serial['events']).forEach((item) => {
                                const line = generateLineForStatistics(projectTemplate, preprocessingResults, item['values']);
                                container.appendChild(line);
                            });
                        });

                        if (container.childElementCount === 0) {
                            container.appendChild(generateLineForStatistics(projectTemplate, preprocessingResults, null));
                        }
                        return container.outerHTML;
                    },
                    renderHTML: true,
                    skipWhileExport: true,
                }
            ]);
        } else {
            baseProperties = baseProperties.concat([
                {
                    label: 'Event ID',
                    renderer(item) {
                        const events = item['events'];
                        const container = elementGenerators.tableCellMultiLines();
                        if (events.length) {
                            events.forEach((event) => {
                                const elem = elementGenerators.tableCellLine(event['event_id'], tableCellLineHeightBase);
                                elem.className = elem.className += ' document-entry document-entry-pointer';
                                elem.dataset.documentEntryFocusEvent = event['event_id'];
                                container.appendChild(elem);
                            });
                        } else {
                            container.appendChild(elementGenerators.tableCellLine('', tableCellLineHeightBase));
                        }
                        return container.outerHTML;
                    },
                    renderHTML: true,
                    filterable: {
                        pipsBuilder(payload) {
                            return [{$match: {'joined_event_objects.event_id': payload.value}}]
                        },
                        labelRenderer(payload) {
                            return `<span>${payload.value}</span>`;
                        },
                        filterForm: TextMessage,
                      involvesJoinedEventObjects: true
                    },
                    sortable: {
                        asc: {'joined_event_objects.event_id': 1},
                        desc: {'joined_event_objects.event_id': -1},
                      involvesJoinedEventObjects: true
                    },
                },
                {
                    label: 'Statistics',
                    renderer(item) {
                        let projectTemplate;

                        projectTemplate = projectIdToTemplate[item['project_id']];

                        if (!projectTemplate) {
                            return '';
                        }

                        const events = item['events'] || [];

                        const preprocessingResults = item['preprocessing_results'] || {};

                        const container = elementGenerators.tableCellMultiLines();

                        if (events.length) {
                            events.forEach((event) => {
                                const line = generateLineForStatistics(projectTemplate, preprocessingResults, event['values']);
                                container.appendChild(line);
                            });
                        } else {
                            container.appendChild(generateLineForStatistics(projectTemplate, preprocessingResults, null));
                        }
                        return container.outerHTML;
                    },
                    renderHTML: true
                }
            ]);
        }

        baseProperties.forEach((v, i) => {
            v.propertyKey = 'BP' + i;
        });
        return baseProperties;
    };

    let getRenderedPropertyCell;

    getRenderedPropertyCell = (enableSerial, item, field) => {
        const fieldId = field.field_id;
        if (enableSerial) {
            const serials = item['serials'] || [];
            const preprocessing = (item['preprocessing_results'] || {})[fieldId] || {};
            const container = elementGenerators.tableCellMultiLines();
            serials.forEach((serial) => {
                if (!serial['events'].length) {
                    container.appendChild(generateLineForTemplateField(null, field, preprocessing));
                    return
                }
                (serial['events']).forEach((item) => {
                    container.appendChild(generateLineForTemplateField(item['values'], field, preprocessing, item['event_id']));
                });
            });
            if (container.childElementCount === 0) {
                container.appendChild(generateLineForTemplateField(null, field, preprocessing));
            }
            return container.outerHTML;
        }
        const events = item['events'];
        const preprocessing = (item['preprocessing_results'] || {})[fieldId] || {};
        const container = elementGenerators.tableCellMultiLines();

        if (events.length) {
            events.forEach((event) => {
                container.appendChild(generateLineForTemplateField(event['values'], field, preprocessing, event['event_id']));
            });
        } else {
            container.appendChild(generateLineForTemplateField(null, field, preprocessing));
        }
        return container.outerHTML;
    }

    const elementGenerators = {
        tableCellLine(textContent, height, className) {
            const elem = document.createElement('div');
            elem.className = 'line' + (className ? (' ' + className) : '');
            elem.style.height = `${height}px`;
            if (textContent) {
                const span = document.createElement('span');
                span.innerText = textContent;
                elem.appendChild(span);
            }
            return elem;
        },
        tableCellMultiLines() {
            const elem = document.createElement('div');
            elem.className = 'multi-lines';
            return elem;
        }
    };

    const getEventPropertyValueCategory = function (values, field, preprocessing) {
        let temp, category;
        const r = {};
        const fieldId = field['field_id'];

        if (values && evaluateFieldValueFilled(values[fieldId], field)) {
            category = propertyValueCategories.filled_confirmed;
        } else if (!values && evaluateFieldValueFilled(preprocessing['auto_filled'], field)) {
            category = propertyValueCategories.auto_filled_unconfirmed;
        } else if ((temp = preprocessing['candidates']) && temp.length) {
            category = propertyValueCategories.unfilled_with_candidates;
            r.nbCategories = temp.length;
        } else {
            category = propertyValueCategories.unfilled_without_candidates;
        }

        r.category = category;
        return r;
    };

    const generateLineForStatistics = function (projectTemplate, preprocessingResults, values) {
        const counts = {
            filled_confirmed: 0,
            auto_filled_unconfirmed: 0,
            unfilled_with_candidates: 0,
            unfilled_without_candidates: 0
        };

        projectTemplate['fields'].forEach(field => {
            const fieldId = field['field_id'];
            const preprocessing = preprocessingResults[fieldId] || {};

            const r = getEventPropertyValueCategory(values, field, preprocessing);

            switch (r.category) {
                case propertyValueCategories.filled_confirmed:
                    counts.filled_confirmed++;
                    break
                case propertyValueCategories.auto_filled_unconfirmed:
                    counts.auto_filled_unconfirmed++;
                    break
                case propertyValueCategories.unfilled_with_candidates:
                    counts.unfilled_with_candidates++;
                    break
                case propertyValueCategories.unfilled_without_candidates:
                    counts.unfilled_without_candidates++;
                    break
            }
        });

        const line = elementGenerators.tableCellLine('', tableCellLineHeightBase);

        const scaleBar = document.createElement('div');
        scaleBar.className = 'scale-bar';
        line.appendChild(scaleBar);

        let totalCount = 0;
        for (let k in counts) {
            if (counts.hasOwnProperty(k)) {
                totalCount += counts[k];
            }
        }

        const innerIs = [
            {
                className: 'bg-legend-filled-confirmed',
                count: counts.filled_confirmed
            },
            {
                className: 'bg-legend-auto-filled-unconfirmed',
                count: counts.auto_filled_unconfirmed
            },
            {
                className: 'bg-legend-unfilled-with-candidates',
                count: counts.unfilled_with_candidates
            },
            {
                className: 'bg-legend-unfilled-without-candidates',
                count: counts.unfilled_without_candidates,
            }
        ];

        innerIs.forEach(v => {
            const elem = document.createElement('i');
            const percentage = v.count / totalCount * 100;
            if (percentage === 0) {
                return;
            }
            elem.className = v.className;
            elem.style.width = percentage + '%';
            scaleBar.appendChild(elem);
            elem.setAttribute('aria-label', parseFloat(percentage.toFixed(2)) + '%');
            elem.setAttribute('data-balloon-pos', 'up');
        });
        return line;
    }

    const generateLineForTemplateField = function (values, field, preprocessing, eventId) {
        let textContent, className;
        const fieldId = field.field_id;

        const r = getEventPropertyValueCategory(values, field, preprocessing);

        switch (r.category) {
            case propertyValueCategories.filled_confirmed:
                textContent = '';
                className = 'bg-legend-filled-confirmed';
                break
            case propertyValueCategories.auto_filled_unconfirmed:
                textContent = '';
                className = 'bg-legend-auto-filled-unconfirmed';
                break
            case propertyValueCategories.unfilled_with_candidates:
                textContent = r.nbCategories;
                className = 'bg-legend-unfilled-with-candidates text-unfilled-with-candidates';
                break
            case propertyValueCategories.unfilled_without_candidates:
                textContent = '';
                className = 'bg-legend-unfilled-without-candidates';
                break
        }

        const line = elementGenerators.tableCellLine(textContent, tableCellLineHeightBase, className);

        if (eventId) {
            line.className += ' document-entry document-entry-of-property';
            line.dataset.documentEntryFocusEvent = eventId;
            line.dataset.documentEntryFocusFieldId = fieldId;
        }

        if (r.category === propertyValueCategories.filled_confirmed) {
            let value = values[fieldId];
            // if (value.constructor === Object) {
            //     value = JSON.stringify(value, undefined, 2);
            // } else {
            //     value = value.toLocaleString();
            // }

            // const maxLen = 50;
            // if (value.substr(maxLen)) {
            //     value = value.substr(0, maxLen) + '...';
            // }
            line.title = renderValueInCandidateItem(value, field);
        }

        return line;
    };

    export default {
        name: "ResultsTableContainer",
        components: {TextMessage, SimpleDropdown},
        methods: {
            checkResettable() {
                let canResetFilters = false;
                let canResetSorter = false;
                if (!deepCompare(this.dominantSorter, this.defaultDominantSorter)) {
                    canResetSorter = true;
                }
                let propertyKey;
                for (propertyKey in this.propertyFilterFormValues) {
                    if (this.propertyFilterFormValues.hasOwnProperty(propertyKey)) {
                        if (this.propertyFilterFormValues[propertyKey]) {
                            canResetFilters = true;
                        }
                    }
                }
                this.$emit('updateCanReset', canResetFilters || canResetSorter);
            },
            resetFiltersAndSorter() {
                this.propertyFilterFormValues = {};

                if (this.dominantSorter === this.defaultDominantSorter) {
                    this.doSearch();
                    this.checkResettable();
                } else {
                    this.dominantSorter = this.defaultDominantSorter
                }
            },
            onRecordCheckboxChange(event, row) {
                if (event.target.checked) {
                    this.addIntoSelectedGlobalDocumentIds(row.global_document_id);
                } else {
                    this.removeFromSelectedGlobalDocumentIds(row.global_document_id);
                }
            },
            selectOrUnselectAllRows(toSelectOrUnselect) {
                const refRecordCheckboxes = this.$refs.recordCheckboxes;
                let global_doc_id;
                refRecordCheckboxes.forEach((v, i) => {
                    // check all or uncheck all
                    if (v.checked !== toSelectOrUnselect) {
                        v.checked = toSelectOrUnselect;
                        global_doc_id = this.rows[i].global_document_id;
                        if (toSelectOrUnselect) {
                            this.addIntoSelectedGlobalDocumentIds(global_doc_id);
                        } else {
                            this.removeFromSelectedGlobalDocumentIds(global_doc_id);
                        }
                    }
                });
            },
            onPropertyFormDoClose(propertyKey) {
                const pk = `filterFormDropdownFor${propertyKey}`;
                let ref;
                ref = this.$refs[pk];
                if (ref && (ref = ref[0])) {
                    ref.toggleDropdownMenu(false);
                }
            },
            onPropertyFormDoSubmit(propertyKey, formValue) {
                this.propertyFilterFormValues[propertyKey] = formValue;
                this.doSearch();
                this.checkResettable();
            },
            onPropertyFilterPopupVisibilityChange(property, v) {
                if (v) {
                    this.activeFilterMenuProperty = property;
                    const pk = `filterFormFor${property.propertyKey}`;
                    let ref = this.$refs[pk];
                    if (ref && (ref = ref[0])) {
                        setTimeout(() => {
                            ref.fillValue(this.propertyFilterFormValues[property.propertyKey]);
                            ref.focus();
                        }, 0);
                    }
                } else {
                    this.activeFilterMenuProperty = null;
                }
            },
            sortByProperty(p, d) {
                const {property, direction} = this.dominantSorter;
                if (p === property && d === direction) {
                    this.dominantSorter = {property: null, direction: null};
                    return;
                }

                this.dominantSorter = {property: p, direction: d};
            },
            getPropertyClassSet(property) {
                let className = property.className || '';

                if (property.sortable) {
                    className += ' data-sortable';
                }
                if (property.filterable) {
                    className += ' data-filterable';
                }
                if (property === this.dominantSorter.property) {
                    className += ' sort-active';
                    if (this.dominantSorter.direction === 'asc') {
                        className += ' sort-active-asc';
                    } else {
                        className += ' sort-active-desc';
                    }
                }
                if (property === this.activeFilterMenuProperty) {
                    className += ' filter-menu-visible';
                }

                if (this.propertyFilterFormValues[property.propertyKey]) {
                    className += ' filter-value-filled';
                }

                return className;
            },
            onClickResultsRow(row, event) {
                const $target = $(event.target);

                if (this.recordsSelectionModeOn) {
                    const selector = 'input[type="checkbox"]';
                    let checkbox;
                    if (!$target.is(selector)) {
                        checkbox = event.currentTarget.querySelector(selector);
                        checkbox.click();
                        checkbox.focus();
                    }
                    return;
                }

                const documentEntry = $target.closest('.document-entry')[0];
                if (documentEntry) {
                    this.eventHub.$emit('openDocument', {
                        globalDocumentId: row['global_document_id'],
                        silent: checkCrossPlatformEventCtrlKey(event),
                        focusEventId: documentEntry.dataset.documentEntryFocusEvent,
                        focusFieldId: documentEntry.dataset.documentEntryFocusFieldId,
                        focusSerialId: documentEntry.dataset.documentEntryFocusSerialId,
                        projectId: this.projectId,
                    });
                }
            },
            scrollTo(x, y) {
                this.$refs.scrollingElement.scrollLeft = x;
                this.$refs.scrollingElement.scrollTop = y;
            },
            onTableScroll(event) {
                this.$refs.fakeTableHeaderContainer.scrollLeft = event.currentTarget.scrollLeft;
            },
            adjustFixedTableHeader() {
                const table = this.$refs.table;
                const fakeTableHeaderContainer = this.$refs.fakeTableHeaderContainer;
                const scrollingElement = this.$refs.scrollingElement;

                if (!table || !table.offsetParent) {
                    return;
                }
                const fakeTable = this.$refs.fakeTable;
                const tableTh = table.tHead.firstElementChild.children;
                const fakeTableTh = fakeTable.tHead.firstElementChild.children;
                let width;

                for (let i = 0; i < tableTh.length; i++) {
                    width = tableTh[i].getBoundingClientRect().width;
                    fakeTableTh[i].style.maxWidth = fakeTableTh[i].style.width = fakeTableTh[i].style.minWidth = width + 'px';
                }

                fakeTableHeaderContainer.style.width = `${scrollingElement.clientWidth}px`;
            },
            getPips() {
              let pips = [];

              let propertyKey;
              let value;
              let property;
              let obj, k, v, $sort;
              let involvedJoinedEventObjects = false;

              for (propertyKey in this.propertyFilterFormValues) {
                if (this.propertyFilterFormValues.hasOwnProperty(propertyKey)) {
                  value = this.propertyFilterFormValues[propertyKey];
                  if (value) {
                    property = this.propertyKeyToItem[propertyKey];
                    pips = pips.concat(property.filterable.pipsBuilder(value));
                    involvedJoinedEventObjects = property.filterable.involvesJoinedEventObjects || involvedJoinedEventObjects;
                  }
                }
                }

                if (this.dominantSorter && this.dominantSorter.property) {
                    property = this.dominantSorter.property;
                    obj = property.sortable[this.dominantSorter.direction];
                    k = Object.keys(obj)[0];
                    v = obj[k];
                    $sort = {
                        '_score_for_not_null': 1,
                        '_score_for_not_empty_array': 1,
                    };
                    $sort[k] = v;
                    pips = pips.concat([
                        {
                            $addFields: {
                                _score_for_not_null: {
                                    $cond: [
                                        {$ifNull: [`$${k}`, false]},
                                        1,
                                        10
                                    ]
                                },
                                _score_for_not_empty_array: {
                                    $cond: [
                                        {
                                            $and: [
                                                {$isArray: `$${k}`},
                                                {$gt: [{$size: `$${k}`}, 0]}
                                            ]
                                        },
                                        1,
                                        10,
                                    ]
                                }
                            }
                        },
                      {
                        $sort,
                      },
                      {
                        $project: {
                          _score_for_not_null: 0,
                          _score_for_not_empty_array: 0,
                        }
                      }
                    ]);
                  involvedJoinedEventObjects = property.sortable.involvesJoinedEventObjects || involvedJoinedEventObjects;
                }

              return {
                pips,
                involvedJoinedEventObjects
              };
            },
            doSearch() {
                this.eventHub.$emit(`searchDocumentsForProject${this.projectId}`, this.getPips());
            },
            addIntoSelectedGlobalDocumentIds(global_doc_id) {
                this.$emit('addIntoSelectedGlobalDocumentIds', global_doc_id)
            },
            removeFromSelectedGlobalDocumentIds(global_doc_id) {
                this.$emit('removeFromSelectedGlobalDocumentIds', global_doc_id)
            },
            adjustRecordCheckboxes() {
                const refRecordCheckboxes = this.$refs.recordCheckboxes;
                const {selectedGlobalDocumentIds} = this;
                let global_doc_id;
                refRecordCheckboxes.forEach((v, i) => {
                    global_doc_id = this.rows[i].global_document_id;
                    v.checked = selectedGlobalDocumentIds.indexOf(global_doc_id) !== -1;
                })
            },
            initializeDefaultSorter() {
                let i = 0, len = this.fullProperties.length;
                let property;
                for (; i < len; i++) {
                    property = this.fullProperties[i];
                    if (property.sortedDefault) {
                        this.defaultDominantSorter = {
                            property,
                            direction: property.sortedDefault.direction,
                        };
                        break;
                    }
                }
            }
        },
        data() {
            return {
                windowResizeListener: null,
                dominantSorter: {
                    property: null,
                    direction: 0,
                },
                activeFilterMenuProperty: null,
                propertyFilterFormValues: {},
                defaultDominantSorter: {property: null, direction: 0},
            }
        },
        props: {
            rows: Array,
            projectId: String,
            rowCounterBase: Number,
            attributePropertyHiddenMaps: Object,
            recordsSelectionModeOn: Boolean,
            selectedGlobalDocumentIds: Array,
        },
        computed: {
            fullProperties() {
                let value = [];

                value = value.concat(getBaseProperties(this.enableSerial));
                value = value.concat(this.attributeProperties);

                return value;
            },
            attributeProperties() {
                let value = [];
                const projectTemplate = this.projectTemplate;
                if (projectTemplate) {
                    projectTemplate['fields'].forEach((field, index) => {
                        const fieldName = field['field_name'];
                        const fieldId = field['field_id'];
                        let filterable = undefined;
                        let className = 'form-property';
                        let tooltipText = `${index + 1}. ${fieldName}`;
                        let tooltipOffset = 16;
                        if (field.is_compulsory) {
                            className += ' form-property-compulsory';
                            tooltipText += ' [Required]'
                        }
                        if (field.algo_powered) {
                            className += ' form-property-intelligent';
                            tooltipText += ' [Algorithms Powered]'
                        }

                        let label = `${index + 1}`;
                        let filterFormLabel = undefined;

                        if (field.field_name === 'Transaction status') {
                            tooltipOffset = 0;
                            filterFormLabel = field.field_name;
                            // className += ' free-width'
                            filterable = {
                                pipsBuilder(payload) {
                                    const map = {
                                        commence: {
                                            'joined_event_objects.values.1': 'Commence'
                                        },
                                        completion: {
                                            'joined_event_objects.values.1': 'Completion'
                                        },
                                        abort: {
                                            'joined_event_objects.values.1': 'Abort'
                                        },
                                        empty: {
                                            'joined_event_objects.values.1': null
                                        },
                                        update: {
                                            'joined_event_objects.values.1': 'Update'
                                        },
                                    };
                                    let $or = payload.selectedItems.map(v => (map[v]));
                                    return [
                                        {
                                            $match: {
                                                $or
                                            }
                                        }
                                    ]
                                },
                                labelRenderer(payload) {
                                    const map = {
                                        commence: 'Commence',
                                        completion: 'Completion',
                                        abort: 'Abort',
                                        empty: '(Empty)',
                                        update: 'Update',
                                    };
                                    return `<span>${payload.selectedItems.map(v => (map[v])).join(', ')}</span>`;
                                },
                                filterForm: CheckBoxes,
                                parameters: {
                                    items: [
                                        {
                                            label: 'Commence',
                                            id: 'commence',
                                        },
                                        {
                                            label: 'Completion',
                                            id: 'completion',
                                        },
                                        {
                                          label: 'Update',
                                          id: 'update',
                                        },
                                        {
                                          label: 'Abort',
                                          id: 'abort',
                                        },
                                        {
                                          label: '(Empty)',
                                          id: 'empty'
                                        }
                                    ]
                                },
                              involvesJoinedEventObjects: true
                            }
                        }

                        value.push({
                            label: label,
                            renderer: (item) => (getRenderedPropertyCell(this.enableSerial, item, field)),
                            renderHTML: true,
                            className: className,
                            tooltipText,
                            propertyKey: `ATTR${index}`,
                            isAttribute: true,
                            fullLabel: `${index + 1}. ${fieldName}`,
                            fieldId,
                            filterable,
                            tooltipOffset,
                            filterFormLabel,
                            field,
                        })
                    })
                }
                return value;
            },
            shownProperties() {
                let value = [];
                const attributePropertyHiddenMaps = this.attributePropertyHiddenMaps;
                this.fullProperties.forEach((v) => {
                    if (!attributePropertyHiddenMaps[v.propertyKey]) {
                        value.push(v);
                    }
                });
                return value;
            },
            propertyKeyToItem() {
                const map = {};

                this.fullProperties.forEach(v => {
                    map[v.propertyKey] = v;
                });

                return map;
            },
            openGlobalDocumentIds() {
                return this.$store.state.openGlobalDocumentIds[this.projectId] || [];
            },
            projectTemplate() {
                return projectIdToTemplate[this.projectId];
            },
            enableSerial() {
                return this.projectTemplate && this.projectTemplate['enable_serial'];
            },
        },
        watch: {
            rows() {
                this.$nextTick(() => {
                    this.adjustFixedTableHeader();
                    if (this.recordsSelectionModeOn) {
                        this.adjustRecordCheckboxes();
                    }
                })
            },
            $route(to, from) {
                if (to.params.pid !== this.projectId) {
                    return;
                }

                if (to.name === 'home' && from.name !== to.name) {
                    this.$nextTick(() => {
                        this.adjustFixedTableHeader();
                    })
                }
            },
            dominantSorter() {
                this.doSearch();
                this.checkResettable();
            },
            shownProperties() {
                this.$nextTick(() => {
                    this.adjustFixedTableHeader();
                })
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.adjustFixedTableHeader();
            });
            this.windowResizeListener = this.adjustFixedTableHeader.bind(this);
            window.addEventListener('resize', this.windowResizeListener);
            this.initializeDefaultSorter();
            this.dominantSorter = this.defaultDominantSorter;
        },
        destroyed() {
            window.removeEventListener('resize', this.windowResizeListener);
        }
    }
</script>

<style lang="scss">
    @import "../assets/variables";

    $table-cell-line-height-base: 36px;


    .results-table-container {
        height: 100%;
        position: relative;
        background-color: white;

        &.selection-mode-on {
            table {
                td {
                    .document-entry {
                        pointer-events: none;
                        text-decoration: none !important;
                    }
                }

                tbody {
                    tr {
                        cursor: pointer;

                        &:hover {
                            background-color: #f0f0f0;

                            .selection-tool {
                                background-color: #f0f0f0;
                            }
                        }

                        &.selected {
                            background-color: #e6e6e6;

                            td {
                                border: 1px solid #f0f0f0;
                                border-bottom: 1px solid #fafafa;
                            }

                            .selection-tool {
                                background-color: #e6e6e6;
                            }
                        }
                    }
                }
            }
        }

        .fake-table-header-container {
            position: absolute;
            top: 0;
            left: 0;
            overflow: hidden;
            z-index: 9;
        }

        .scrolling-element {
            overflow: auto;
            height: 100%;
        }

      .final-results-indicator {
        margin-left: 0.25rem;
        user-select: none;
        color: gray;
      }

        table {
            min-width: 100%;
            border-collapse: collapse;

            label {
                display: inline-flex;
                align-items: center;
            }

            th, td {
                padding: 0;

                .selection-tool {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }

            thead {
                th {
                    font-weight: 600;
                    background-color: #e3e1ee;
                    color: darken($primary-purple, 8);
                    white-space: nowrap;
                    border: 1px solid #ededee;
                    padding: 0.25rem;
                    font-size: 0.95em;
                    text-align: center;

                    .content {
                        position: relative;
                        height: 2.5rem;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        min-width: 24px;
                    }

                    .sortable-indicator {
                        position: absolute;
                        display: none;
                        right: 0;
                        top: 50%;
                        margin-top: -13px;

                        .vertical-container {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        }

                        .sort-asc, .sort-desc {
                            transform: scale(0.55);
                            color: rgba(black, 0.25);
                        }

                        .sort-asc {
                            transform-origin: bottom center;
                        }

                        .sort-desc {
                            transform-origin: top center;
                        }
                    }

                    .operation-layer {
                        display: none;
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        bottom: 0;
                        background-color: #e3e1ee;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        animation: fadeIn 100ms ease-in;

                        .buttons {
                            display: flex;
                            align-items: center;
                            margin-top: 0.1rem;
                            height: 18px;

                            i {
                                margin: 0 0.15rem;
                                cursor: pointer;
                            }

                            .sort-asc, .sort-desc {
                                transform: scale(0.7);
                                color: rgba(black, 0.25);
                            }

                            .button-filter {
                                color: rgba(black, 0.25);
                            }
                        }
                    }

                    .filter-label {
                        font-size: 0.86em;
                        font-weight: 300;
                        color: #B661DA;
                        height: 18px;
                        margin-top: 0.1rem;
                        max-width: 100%;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                    }

                    &.data-sortable {
                        .content {
                            padding-right: 16px;
                            padding-left: 16px;
                        }

                        .sortable-indicator {
                            display: block;
                        }
                    }

                    &.data-filterable, &.data-sortable {
                        &:hover, &.filter-menu-visible {
                            .operation-layer {
                                display: flex;
                            }
                        }
                    }

                    &.sort-active {
                        &.sort-active-asc {
                            .sort-asc {
                                color: inherit;
                            }
                        }

                        &.sort-active-desc {
                            .sort-desc {
                                color: inherit;
                            }
                        }
                    }

                    &.filter-value-filled {
                        .operation-layer {
                            .buttons {
                                .button-filter {
                                    color: inherit;
                                }
                            }
                        }
                    }
                }
            }

            th, td {
                &.form-property {
                    max-width: 50px;
                    text-align: center;

                    &.free-width {
                        max-width: none;

                        .content {
                            .text {
                                padding: 0;
                                box-shadow: none;
                                background-color: transparent;
                            }
                        }
                    }
                }
            }

            th.form-property {
                .content {
                    .text {
                        background-color: rgba(white, 0.6);
                        border-radius: 8px;
                        min-width: 1.2em;
                        line-height: 1.2em;
                        box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
                        display: inline-block;
                        padding: 0 4px;
                        flex: 0 0 auto;
                    }
                }

                &.form-property-compulsory {
                    .content {
                        .text {
                            padding-left: 6px;

                            &:after {
                                content: '*';
                                color: black;
                            }
                        }
                    }
                }

                &.form-property-intelligent {
                    .content {
                        .text {
                            background-color: #c8c7d9;
                            box-shadow: 1px 1px 1px rgba(black, 0.4);
                            text-shadow: 0 0 1px white;
                        }
                    }
                }
            }

            tbody {
                tr:hover {
                    background-color: #f3f3f3;

                    td {
                        .document-entry.document-entry-pointer {
                            text-decoration: underline;
                        }
                    }
                }

                tr.open {
                    .document-entry.document-entry-pointer.document-entry-pointer-dominant {
                        box-shadow: 0 0 0 3px rgba($primary-blue, 0.6);
                        border-radius: 999px;
                        text-decoration: none;

                        &:hover {
                            box-shadow: 0 0 0 3px rgba($primary-blue, 0.8);
                        }
                    }
                }

                td {
                    border: 1px solid #ededee;

                    .selection-tool {
                        background-color: white;
                    }

                    .document-entry.document-entry-of-property {
                        cursor: pointer;

                        &.bg-legend-unfilled-without-candidates {
                            &:hover {
                                background-color: lighten($scale-color-unfilled-without-candidates, 6);
                                box-shadow: inset 0 0 0 1px $scale-color-unfilled-without-candidates;
                            }
                        }

                        &.bg-legend-unfilled-with-candidates {
                            &:hover {
                                background-color: lighten($scale-color-unfilled-with-candidates, 6);
                                box-shadow: inset 0 0 0 1px $scale-color-unfilled-with-candidates;
                            }
                        }

                        &.bg-legend-auto-filled-unconfirmed {
                            &:hover {
                                background-color: lighten($scale-color-auto-filled-unconfirmed, 6);
                                box-shadow: inset 0 0 0 1px $scale-color-auto-filled-unconfirmed;
                            }
                        }

                        &.bg-legend-filled-confirmed {
                            &:hover {
                                background-color: lighten($scale-color-filled-confirmed, 6);
                                box-shadow: inset 0 0 0 1px $scale-color-filled-confirmed;
                            }
                        }
                    }

                    .document-entry.document-entry-pointer {
                        cursor: pointer;

                        &:hover {
                            color: $primary-purple;
                        }
                    }

                    .line {
                        padding: 0 0.5rem;
                        white-space: nowrap;
                        height: $table-cell-line-height-base;
                        display: flex;
                        align-items: center;
                    }

                    &.text-center {
                        .line {
                            justify-content: center;
                        }
                    }

                    &.text-left {
                        .line {
                            justify-content: flex-start;
                        }
                    }

                    &.text-right {
                        .line {
                            justify-content: flex-end;
                        }
                    }

                    .multi-lines {
                        .line {
                            height: $table-cell-line-height-base;
                            display: flex;
                            align-items: center;
                            padding: 0 0.5rem;

                            & > * {
                                flex: 1 auto;
                            }

                            &:not(:last-child) {
                                border-bottom: 1px solid #ededee;
                            }
                        }
                    }

                    .scale-bar {
                        height: 5px;
                        border-radius: 999px;
                        display: flex;
                        /*overflow: hidden;*/
                        width: 100px;
                        margin: 0 0.5rem;

                        i {
                            height: 100%;
                            cursor: default;

                            &:first-child {
                                border-radius: 999px 0 0 999px;
                            }

                            &:last-child {
                                border-radius: 0 999px 999px 0;
                            }

                            &:first-child:last-child {
                                border-radius: 999px;
                            }
                        }
                    }
                }
            }
        }
    }

    .tbody-mask {
        position: absolute;
        top: 50px;
        right: 0;
        bottom: 0;
        left: 0;
        background: black;
        opacity: 0;
        pointer-events: none;
        border-radius: 4px;
        transition: opacity 0.3s ease;
        z-index: 1;
    }

    .results-table-container.filter-menu-visible {
        .tbody-mask {
            opacity: 0.3;
        }
    }
</style>
<style lang="scss">
    .filter-form {
        padding: 0.25rem 0.8rem;

        .filter-form-header {
            font-weight: 500;
            margin-bottom: 0.8rem;
            padding-right: 1rem;
        }

        .filter-form-body {
            .checkboxes {
                .status-icon {
                    font-size: 1.05em;
                    margin-right: 0.25rem;
                }

                .status-text {
                    font-size: 0.875em;
                }
            }
        }
    }
</style>
